/**
 * Cursor Bundle Definitions
 *
 * Pre-built themed cosmetic packs for the typing caret. Each bundle is a
 * CSS-class identifier; styling lives in cursorBundles.css. See
 * STORE_SYSTEM.md §4.1.
 */

import type { Rarity, Acquisition } from './rarity';

export interface CursorBundle {
  id: string;
  name: string;
  description: string;
  price: number;
  rarity: Rarity;
  /** Omit for buyable ('shop'); 'earned' = challenge/box only. */
  acquisition?: Acquisition;
  /** CSS class applied on the cursor element. Empty string for default. */
  cssClass: string;
  /** Solid color used as a swatch in previews / fallback rendering. */
  swatchColor: string;
  /** True for the free default bundle every user has. */
  isDefault?: boolean;
}

export const DEFAULT_CURSOR_BUNDLE_ID = 'default';

export const CURSOR_BUNDLES: CursorBundle[] = [
  {
    id: 'default',
    name: 'Classic Blue',
    description: 'The default cursor. Always equipped, free.',
    price: 0,
    rarity: 'common',
    cssClass: '',
    swatchColor: '#60a5fa',
    isDefault: true,
  },
  {
    id: 'minimalist-pro',
    name: 'Minimalist Pro',
    description: 'A refined slate underline. No animation — just clean.',
    price: 60,
    rarity: 'common',
    cssClass: 'cursor-bundle-minimalist',
    swatchColor: '#cbd5e1',
  },
  {
    id: 'retro-8bit',
    name: 'Retro 8-bit',
    description: 'Chunky pixel block. Blinks like a 1986 terminal.',
    price: 80,
    rarity: 'common',
    cssClass: 'cursor-bundle-retro',
    swatchColor: '#22c55e',
  },
  {
    id: 'neon-arcade',
    name: 'Neon Arcade',
    description: 'Electric magenta with a glowing trail.',
    price: 100,
    rarity: 'rare',
    cssClass: 'cursor-bundle-neon',
    swatchColor: '#ec4899',
  },
  {
    id: 'royal-court',
    name: 'Royal Court',
    description: 'Gold underline with a slow regal pulse.',
    price: 120,
    rarity: 'rare',
    cssClass: 'cursor-bundle-royal',
    swatchColor: '#facc15',
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    description: 'Cyan-magenta glitch underline. For night drives only.',
    price: 150,
    rarity: 'epic',
    cssClass: 'cursor-bundle-cyberpunk',
    swatchColor: '#06b6d4',
  },
];

export function getCursorBundle(id: string | undefined): CursorBundle {
  return CURSOR_BUNDLES.find(b => b.id === id) ?? CURSOR_BUNDLES[0];
}
